import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AppDispatch, RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { actions } from "features/builder/slice";
import { AccountType } from "features/user/types";
import { usePieceListScroll } from "features/builder/hook";
import { TOP_GUIDE_CHAPTERS } from "features/builder/guideBookPages";
import {
  viewPiece,
  copyPiece,
  reportPiece,
  toggleFavorite,
  getPublicPieces,
} from "features/builder/api";
import {
  MockPiece,
  PieceListContext,
  PieceCardAction,
  PieceLayoutMode,
  PublicOfficialFilter,
  POPULAR_THRESHOLD,
  comparePieces,
  toCardPiece,
} from "features/builder/pieceList";
import {
  GuideBookId,
  BuilderGuideBook,
  MapCard,
  MapListItem,
  MapDetailPage,
  BuilderTutorialCard,
  PublicMapsHeader,
  BuilderSidebar,
  SidebarSection,
  LayoutModeMenu,
  LAYOUT_GRID_CLASS,
  BuilderConfirmDialog,
  BuilderAccountRequiredDialog,
  BuilderModalAccent,
} from "features/builder/elements";
import { GuideTargetId } from "./PieceBuilderTopGuideOverlay";

export const PublicMapsTab = ({
  onOpenImage,
  onDetailChange,
}: {
  onOpenImage: (src: string, alt: string) => void;
  onDetailChange?: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const active = useSelector((state: RootState) => state.user.active);
  const [pieces, setPieces] = useState<MockPiece[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    onDetailChange?.(selectedId !== null);
  }, [selectedId, onDetailChange]);
  const { sortAxis, sortOrder, filter, layoutMode } = useSelector(
    (state: RootState) => state.builder.listUi.public,
  );
  const [copyTarget, setCopyTarget] = useState<MockPiece | null>(null);
  const [reportTarget, setReportTarget] = useState<MockPiece | null>(null);
  const [showReportDenied, setShowReportDenied] = useState(false);
  const { ref: scrollCallbackRef, onScroll } = usePieceListScroll(
    PieceListContext.PUBLIC,
    pieces.length > 0,
  );

  const load = useCallback(async () => {
    try {
      const items = await getPublicPieces({ uid: active?.uid });
      setPieces(items.map(toCardPiece));
    } catch {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_LOAD_FAILED")}</p>
        </div>,
      );
    }
  }, [active?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleMaps = useMemo(() => {
    let list = [...pieces];
    list = list.filter(
      (m) =>
        (filter.official === PublicOfficialFilter.ALL ||
          (filter.official === PublicOfficialFilter.OFFICIAL) === m.is_official) &&
        (!filter.favorite || m.is_liked) &&
        (!filter.copyable || m.enable_copied) &&
        (!filter.remix || m.copied_piece_id !== null) &&
        (!filter.popular || m.liked >= POPULAR_THRESHOLD) &&
        (!filter.commentable || m.enable_comment),
    );
    list.sort(comparePieces(sortAxis, sortOrder));
    return list;
  }, [pieces, filter, sortAxis, sortOrder]);

  const selected = pieces.find((m) => m.piece_id === selectedId) ?? null;

  const handleAction = (piece: MockPiece) => (action: PieceCardAction) => {
    switch (action) {
      case PieceCardAction.DETAIL:
        setSelectedId(piece.piece_id);
        // 閲覧記録（公開ピース）。失敗は無視
        viewPiece({ piece_id: piece.piece_id, uid: active?.uid }).catch(() => undefined);
        break;
      case PieceCardAction.PLAY:
        if (!active?.uid) break;
        // 読み込みはプレビュー画面側のローダー（URL の piece_id）が行う
        navigate(`/courses/builder/${piece.piece_id}/preview?from=public`);
        break;
      case PieceCardAction.COPY:
        setCopyTarget(piece);
        break;
      case PieceCardAction.LIKE:
        if (active?.uid) {
          toggleFavorite({ uid: active.uid, piece_id: piece.piece_id })
            .then(({ is_liked }) => {
              setPieces((prev) =>
                prev.map((m) =>
                  m.piece_id === piece.piece_id
                    ? { ...m, is_liked, liked: Math.max(0, m.liked + (is_liked ? 1 : -1)) }
                    : m,
                ),
              );
              if (is_liked)
                toast.success(
                  <div>
                    <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_LIKE_TOAST")}</p>
                  </div>,
                );
            })
            .catch(() =>
              toast.warn(
                <div>
                  <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
                </div>,
              ),
            );
        }
        break;
      case PieceCardAction.REACTION:
        // リアクションは詳細ページで行う
        setSelectedId(piece.piece_id);
        break;
      case PieceCardAction.REPORT:
        // ゲストは通報できない。API を叩いてから断るのではなく、押した時点で理由を出す
        if (active?.account_type === AccountType.GUEST) {
          setShowReportDenied(true);
          break;
        }
        setReportTarget(piece);
        break;
      default:
        break;
    }
  };

  const handleConfirmCopy = async () => {
    if (!copyTarget || !active?.uid) return;
    const target = copyTarget;
    setCopyTarget(null);
    try {
      await copyPiece({ piece_id: target.piece_id, uid: active.uid });
      toast.success(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_COPY_TOAST")}</p>
        </div>,
      );
    } catch {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
        </div>,
      );
    }
  };

  const handleConfirmReport = () => {
    const target = reportTarget;
    setReportTarget(null);
    if (!target || !active?.uid) return;
    // コメントの通報と同じく、理由は選ばせず other で送る
    reportPiece({ piece_id: target.piece_id, uid: active.uid, reason: "other" })
      .then(() =>
        toast.success(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_REPORT_THANKS")}</p>
          </div>,
        ),
      )
      .catch((error) =>
        toast.warn(
          <div>
            <p className="text text-textcolor-black">
              {I18n.t(
                axios.isAxiosError(error) && error.response?.status === 403
                  ? "MSG_BUILDER_REPORT_NEED_ACCOUNT"
                  : "MSG_BUILDER_ACTION_FAILED",
              )}
            </p>
          </div>,
        ),
      );
  };

  // 一覧と詳細のどちらからでも開くので、確認ダイアログは共通で持つ
  const dialogs = (
    <>
      {/* コピー確認 */}
      <BuilderConfirmDialog
        visible={copyTarget != null}
        accent={BuilderModalAccent.BLUE}
        icon="content_copy"
        confirmIcon="content_copy"
        confirmLabel={I18n.t("MSG_CONFIRM_BTN")}
        confirmSound={`${Constants.assetHost}/assets/blockly/start.mp3`}
        title={I18n.t("MSG_BUILDER_MENU_COPY")}
        message={copyTarget ? `「${copyTarget.name}」` : ""}
        close={() => setCopyTarget(null)}
        handleConfirm={handleConfirmCopy}
      />

      {/* 通報確認 */}
      {/* ゲストは通報できない。押した時点で理由を出す */}
      <BuilderAccountRequiredDialog
        visible={showReportDenied}
        accent={BuilderModalAccent.ORANGE}
        icon="flag"
        title={I18n.t("MSG_BUILDER_MENU_REPORT")}
        message={I18n.t("MSG_BUILDER_REPORT_NEED_ACCOUNT")}
        close={() => setShowReportDenied(false)}
      />

      <BuilderConfirmDialog
        visible={reportTarget != null}
        accent={BuilderModalAccent.ORANGE}
        icon="flag"
        confirmIcon="flag"
        confirmLabel={I18n.t("MSG_CONFIRM_BTN")}
        confirmSound={`${Constants.assetHost}/assets/sounds/pop-up-off.mp3`}
        title={I18n.t("MSG_BUILDER_MENU_REPORT")}
        message={reportTarget ? `「${reportTarget.name}」` : ""}
        close={() => setReportTarget(null)}
        handleConfirm={handleConfirmReport}
      />
    </>
  );

  // 詳細ページでも確認ダイアログを描画する必要があるので、早期 return はしない。
  // 以前は詳細で「ほうこくする」「コピーしてつくる」を押しても何も起きなかった
  if (selected) {
    return (
      <>
        <MapDetailPage
          piece={selected}
          context={PieceListContext.PUBLIC}
          onBack={() => setSelectedId(null)}
          onAction={handleAction(selected)}
          onOpenImage={onOpenImage}
        />
        {dialogs}
      </>
    );
  }

  return (
    <div className="flex-row-el relative w-full h-full">
      <BuilderSidebar>
        <PublicMapsHeader
          count={visibleMaps.length}
          filter={filter}
          sortAxis={sortAxis}
          sortOrder={sortOrder}
          onChangeFilter={(next) => dispatch(actions.setPublicFilter(next))}
          onChangeSort={(axis, order) =>
            dispatch(actions.setListSort({ context: PieceListContext.PUBLIC, axis, order }))
          }
        />
        <SidebarSection
          icon="grid_view"
          labelKey="MSG_BUILDER_SECTION_LAYOUT"
          accentClass="text-blue-100"
          divider={false}
        >
          <LayoutModeMenu
            value={layoutMode}
            accentTextClass="text-blue-100"
            accentBgClass="bg-blue-100"
            accentBorderClass="border-blue-100"
            onChange={(mode) =>
              dispatch(actions.setListLayoutMode({ context: PieceListContext.PUBLIC, mode }))
            }
          />
        </SidebarSection>
      </BuilderSidebar>

      <section className="flex-col-el relative flex-1 h-full p-4">
        <div
          ref={scrollCallbackRef}
          onScroll={onScroll}
          className="flex-col-el flex-1 w-full overflow-y-auto invisible-scrollbar"
        >
          <div className={`${LAYOUT_GRID_CLASS[layoutMode]} w-full pb-4`}>
            <BuilderTutorialCard layoutMode={layoutMode} onClick={() => setShowGuide(true)} />
            {visibleMaps.map((m, index) =>
              layoutMode === PieceLayoutMode.LIST ? (
                <MapListItem
                  key={m.piece_id}
                  id={index === 0 ? GuideTargetId.LIST_ITEM : undefined}
                  piece={m}
                  context={PieceListContext.PUBLIC}
                  onAction={handleAction(m)}
                />
              ) : (
                <MapCard
                  key={m.piece_id}
                  id={index === 0 ? GuideTargetId.LIST_ITEM : undefined}
                  piece={m}
                  context={PieceListContext.PUBLIC}
                  onAction={handleAction(m)}
                  compact={layoutMode === PieceLayoutMode.GRID_2}
                  heroPlay={layoutMode === PieceLayoutMode.GRID_1}
                />
              ),
            )}
          </div>
        </div>
      </section>

      {dialogs}

      <BuilderGuideBook
        open={showGuide}
        book={GuideBookId.TOP}
        chapters={TOP_GUIDE_CHAPTERS}
        onClose={() => setShowGuide(false)}
      />
    </div>
  );
};
